
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { supabase } from '@/lib/customSupabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Trash2, DollarSign, Calendar, Search, Loader2, X, CreditCard, ArrowDownToLine, TrendingDown, TrendingUp, Landmark, Percent, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Helmet } from 'react-helmet';
import SubaccountSwitcher from '@/components/SubaccountSwitcher';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const parseSafeNumber = (val) => {
  if (!val) return 0;
  if (typeof val === 'string') return Number(val.replace(',', '.'));
  return Number(val);
};

const PlatformAdjustmentsPage = ({ onClose }) => {
  const { activeAccountId } = useAuth();
  const { toast } = useToast();
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTerm2, setSearchTerm2] = useState('');
  const [searchOperator, setSearchOperator] = useState('OR');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [summary, setSummary] = useState({ total: 0, extras: 0, count: 0 });
  const [hasSearched, setHasSearched] = useState(false);
  const [financialSummary, setFinancialSummary] = useState({ sales: 0, receipts: 0, expenses: 0, saldosExtras: 0, net: 0, percentage: 0 });

  const fetchAdjustments = useCallback(async () => {
    if (!activeAccountId) return;
    setLoading(true);
    setHasSearched(true);
    try {
      // 1. Buscar Despesas
      let query = supabase
        .from('platform_adjustments')
        .select('*, operators(name)')
        .eq('user_id', activeAccountId)
        .order('data_ocorrencia', { ascending: false });

      const buildOrQuery = (term) => {
        const safeTerm = term.replace(/"/g, ''); // Evita quebrar as aspas do PostgREST
        let condition = `descricao.ilike."%${safeTerm}%",codigo_pedido.ilike."%${safeTerm}%"`;
        const num = parseSafeNumber(term);
        if (!isNaN(num) && term.trim() !== '') {
          const absNum = Math.abs(num);
          condition += `,valor_desconto.eq.${absNum},valor_desconto.eq.-${absNum}`;
        }
        return condition;
      };

      if (searchTerm && searchTerm2) {
        if (searchOperator === 'AND') {
          query = query.or(buildOrQuery(searchTerm));
          query = query.or(buildOrQuery(searchTerm2));
        } else if (searchOperator === 'EXCEPT') {
          query = query.or(buildOrQuery(searchTerm));
          query = query.not('descricao', 'ilike', `%${searchTerm2}%`);
          query = query.not('codigo_pedido', 'ilike', `%${searchTerm2}%`);
          const num2 = parseSafeNumber(searchTerm2);
          if (!isNaN(num2) && searchTerm2.trim() !== '') {
            const absNum2 = Math.abs(num2);
            query = query.not('valor_desconto', 'eq', absNum2).not('valor_desconto', 'eq', -absNum2);
          }
        } else {
          // OR operator
          const orStr1 = buildOrQuery(searchTerm);
          const orStr2 = buildOrQuery(searchTerm2);
          query = query.or(`${orStr1},${orStr2}`);
        }
      } else if (searchTerm) {
        query = query.or(buildOrQuery(searchTerm));
      } else if (searchTerm2) {
        if (searchOperator === 'EXCEPT') {
          query = query.not('descricao', 'ilike', `%${searchTerm2}%`);
          query = query.not('codigo_pedido', 'ilike', `%${searchTerm2}%`);
          const num2 = parseSafeNumber(searchTerm2);
          if (!isNaN(num2) && searchTerm2.trim() !== '') {
            const absNum2 = Math.abs(num2);
            query = query.not('valor_desconto', 'eq', absNum2).not('valor_desconto', 'eq', -absNum2);
          }
        } else {
          query = query.or(buildOrQuery(searchTerm2));
        }
      }
      if (startDate) {
        query = query.gte('data_ocorrencia', startDate);
      }
      if (endDate) {
        query = query.lte('data_ocorrencia', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      setAdjustments(data || []);



      const rawExpensesNegative = (data || []).reduce((acc, adj) => {
        const val = parseSafeNumber(adj.valor_desconto);
        return val < 0 ? acc + val : acc;
      }, 0);
      const rawExpensesPositive = (data || []).reduce((acc, adj) => {
        const val = parseSafeNumber(adj.valor_desconto);
        return val > 0 ? acc + val : acc;
      }, 0);

      const expensesTotal = Math.abs(rawExpensesNegative);
      const saldosExtrasTotal = rawExpensesPositive;

      setSummary({ total: expensesTotal, extras: saldosExtrasTotal, count: (data || []).length });

      // 2. Buscar Vendas/Recebimentos do IFood no período
      let salesQuery = supabase
        .from('sales')
        .select('valorcompra, valorrecebido')
        .eq('user_id', activeAccountId)
        .or('empresa.ilike.%ifood%,bandeira.ilike.%ifood%');

      // Usa datapgt conforme a Solução 2 (Visão de Caixa/Lote)
      if (startDate) salesQuery = salesQuery.gte('datapgt', startDate);
      if (endDate) salesQuery = salesQuery.lte('datapgt', endDate);

      const { data: salesData, error: salesError } = await salesQuery;
      if (salesError) throw salesError;

      const salesTotal = (salesData || []).reduce((acc, s) => acc + Number(s.valorcompra || 0), 0);
      const receiptsTotal = (salesData || []).reduce((acc, s) => acc + Number(s.valorrecebido || 0), 0);
      
      const net = receiptsTotal + saldosExtrasTotal - expensesTotal;
      const percentage = salesTotal > 0 ? (net / salesTotal) * 100 : 0;

      setFinancialSummary({
        sales: salesTotal,
        receipts: receiptsTotal,
        expenses: expensesTotal,
        saldosExtras: saldosExtrasTotal,
        net,
        percentage
      });
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      toast({ title: "Erro", description: "Falha ao carregar os dados financeiros.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [activeAccountId, searchTerm, searchTerm2, searchOperator, startDate, endDate, toast]);

  useEffect(() => {
    // Tela abre zerada. Não faz fetch automático ao entrar.
    setLoading(false);
  }, [activeAccountId]);

  const handleDelete = async (id) => {
    const { error } = await supabase.from('platform_adjustments').delete().eq('id', id);
    if (error) {
      toast({ title: "Erro", description: "Falha ao excluir despesa.", variant: "destructive" });
    } else {
      toast({ title: "Sucesso", description: "Despesa removida." });
      fetchAdjustments();
    }
  };

  const handleDeleteBatch = async () => {
    if (selectedIds.length === 0) return;
    const { error } = await supabase.from('platform_adjustments').delete().in('id', selectedIds);
    if (error) {
      toast({ title: "Erro", description: "Falha ao excluir despesas selecionadas.", variant: "destructive" });
    } else {
      toast({ title: "Sucesso", description: `${selectedIds.length} despesa(s) removida(s).` });
      setSelectedIds([]);
      fetchAdjustments();
    }
  };

  const handleDeleteAll = async () => {
    const { error } = await supabase.from('platform_adjustments').delete().eq('user_id', activeAccountId);
    if (error) {
      toast({ title: "Erro", description: "Falha ao excluir todas as despesas.", variant: "destructive" });
    } else {
      toast({ title: "Sucesso", description: "Todas as despesas foram removidas." });
      setSelectedIds([]);
      fetchAdjustments();
    }
  };

  const exportToPDF = useCallback(() => {
    if (!adjustments || adjustments.length === 0) {
      toast({ title: "Erro", description: "Não há dados para exportar.", variant: "destructive" });
      return;
    }

    try {
      const doc = new jsPDF({ orientation: 'landscape' });
      doc.setFontSize(20);
      doc.text('Saldos e Despesas do IFood', 14, 22);

      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Data da análise: ${new Date().toLocaleDateString('pt-BR')}`, 14, 30);

      // Função auxiliar para desenhar cards
      const drawCard = (x, y, title, value, titleColor, valueColor, bgColor = null) => {
        const width = 50;
        const height = 18;
        
        // Fundo e borda
        if (bgColor) {
          doc.setFillColor(bgColor[0], bgColor[1], bgColor[2]);
          doc.setDrawColor(bgColor[0]-20, bgColor[1]-20, bgColor[2]-20);
          doc.rect(x, y, width, height, 'FD');
        } else {
          doc.setDrawColor(220, 220, 220);
          doc.rect(x, y, width, height, 'S');
        }

        // Título do card
        doc.setFontSize(9);
        doc.setTextColor(titleColor[0], titleColor[1], titleColor[2]);
        doc.text(title, x + 4, y + 7);

        // Valor do card
        doc.setFontSize(12);
        doc.setTextColor(valueColor[0], valueColor[1], valueColor[2]);
        doc.setFont(undefined, 'bold');
        doc.text(value, x + 4, y + 14);
        doc.setFont(undefined, 'normal'); // reseta fonte
      };

      const startY = 35;
      drawCard(14, startY, 'Vendas', formatCurrency(financialSummary.sales), [100,100,100], [80,80,80]);
      drawCard(68, startY, 'Recebimentos', formatCurrency(financialSummary.receipts), [100,100,100], [59, 130, 246]);
      drawCard(122, startY, 'Saldos Extras', formatCurrency(financialSummary.saldosExtras), [100,100,100], [34, 197, 94]);
      drawCard(176, startY, 'Despesas Extras', formatCurrency(financialSummary.expenses), [100,100,100], [239, 68, 68]);
      drawCard(230, startY, 'Líquido no Banco', formatCurrency(financialSummary.net), [21, 128, 61], [21, 128, 61], [220, 252, 231]);

      const headers = [['Data', 'Descrição', 'Pedido Associado', 'Operadora', 'Valor']];
      
      const body = adjustments.map(adj => {
        const isPositive = parseSafeNumber(adj.valor_desconto) > 0;
        const valorStr = (isPositive ? '+' : '') + formatCurrency(adj.valor_desconto);
        
        return [
          formatDate(adj.data_ocorrencia) || '—',
          adj.descricao || '—',
          adj.codigo_pedido || '—',
          adj.operators?.name || '—',
          valorStr
        ];
      });

      doc.autoTable({
        head: headers,
        body: body,
        startY: 60,
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [41, 128, 185], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        didParseCell: function (data) {
          if (data.section === 'body' && data.column.index === 4) {
            const valStr = data.cell.raw;
            if (valStr.includes('+')) {
              data.cell.styles.textColor = [21, 128, 61]; // Verde escuro (text-green-800)
              data.cell.styles.fontStyle = 'bold';
            } else {
              data.cell.styles.textColor = [220, 38, 38]; // Vermelho (text-red-600)
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });

      doc.save(`despesas_plataformas_${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast({
        title: "Exportação concluída!",
        description: `Exportado com sucesso para PDF.`,
      });
    } catch (error) {
      console.error('Erro na exportação PDF:', error);
      toast({ title: "Erro", description: "Falha ao gerar o PDF.", variant: "destructive" });
    }
  }, [adjustments, financialSummary, toast]);


  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === adjustments.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(adjustments.map(a => a.id));
    }
  };

  const formatCurrency = (value) => {
    const num = parseSafeNumber(value);
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [y, m, d] = dateStr.split('-');
    return `${d}/${m}/${y}`;
  };

  return (
    <>
      <Helmet>
        <title>Despesas IFood - Conciliador de Cartões</title>
        <meta name="description" content="Gerencie despesas e taxas de plataformas como iFood." />
      </Helmet>
      <div className="min-h-screen text-white p-4 sm:p-6 lg:p-8">
        <header className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500">
              Despesas IFood
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <SubaccountSwitcher />
          </div>
        </header>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <Card className="bg-white/5 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">Vendas</CardTitle>
              <CreditCard className="h-4 w-4 text-gray-300" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-gray-300">
                {hasSearched ? formatCurrency(financialSummary.sales) : 'R$ 0,00'}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">Recebimentos</CardTitle>
              <ArrowDownToLine className="h-4 w-4 text-blue-400" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-blue-400">
                {hasSearched ? formatCurrency(financialSummary.receipts) : 'R$ 0,00'}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">Saldos Extras</CardTitle>
              <TrendingUp className="h-4 w-4 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-green-400">
                {hasSearched ? formatCurrency(financialSummary.saldosExtras) : 'R$ 0,00'}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">Despesas Extras</CardTitle>
              <TrendingDown className="h-4 w-4 text-red-400" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-red-400">
                {hasSearched ? formatCurrency(financialSummary.expenses) : 'R$ 0,00'}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-green-500/10 border-green-500/30">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-green-400">Líquido no Banco</CardTitle>
              <Landmark className="h-4 w-4 text-green-400" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-green-400">
                {hasSearched ? formatCurrency(financialSummary.net) : 'R$ 0,00'}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/5 border-white/10">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium text-gray-400">% Recebida</CardTitle>
              <Percent className="h-4 w-4 text-orange-400" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-orange-400">
                {hasSearched ? financialSummary.percentage.toFixed(1) + '%' : '0.0%'}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex flex-col xl:flex-row gap-3 mb-4">
          <div className="flex flex-1 gap-2 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Busca 1 (Ex: Mensalidade)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-w-0"
              />
            </div>
            
            <Select value={searchOperator} onValueChange={setSearchOperator}>
              <SelectTrigger 
                className={`w-[110px] shrink-0 border-white/10 ${
                  searchOperator === 'EXCEPT' 
                    ? 'bg-red-500/10 text-red-400 border-red-500/30' 
                    : 'bg-white/5 text-gray-300'
                }`}
              >
                <SelectValue placeholder="Operador" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="OR">OU</SelectItem>
                <SelectItem value="AND">E</SelectItem>
                <SelectItem value="EXCEPT">EXCETO</SelectItem>
              </SelectContent>
            </Select>

            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Busca 2 (Ex: Taxa)..."
                value={searchTerm2}
                onChange={(e) => setSearchTerm2(e.target.value)}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500 min-w-0"
              />
            </div>
          </div>
          <div className="flex gap-2 shrink-0 overflow-x-auto pb-1 xl:pb-0">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-36 bg-white/5 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:filter-[invert(1)]"
              title="Data inicial"
            />
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-36 bg-white/5 border-white/10 text-white [&::-webkit-calendar-picker-indicator]:filter-[invert(1)]"
              title="Data final"
            />
            <Button
              onClick={fetchAdjustments}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
              Filtrar
            </Button>
            {(searchTerm || searchTerm2 || startDate || endDate || hasSearched) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setSearchTerm2('');
                  setSearchOperator('OR');
                  setStartDate('');
                  setEndDate('');
                  setHasSearched(false);
                  setAdjustments([]);
                  setFinancialSummary({ sales: 0, receipts: 0, expenses: 0, net: 0, percentage: 0 });
                }}
                className="text-gray-400 hover:text-white"
                title="Limpar filtros"
              >
                Limpar filtros
              </Button>
            )}
            {adjustments.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={exportToPDF}
                className="bg-blue-600/10 text-blue-400 border-blue-600/30 hover:bg-blue-600/20"
                title="Exportar para PDF"
              >
                <FileText className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {selectedIds.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Trash2 className="w-4 h-4 mr-2" /> Excluir Selecionados ({selectedIds.length})
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                    <AlertDialogDescription>
                      Tem certeza que deseja excluir {selectedIds.length} despesa(s) selecionada(s)?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteBatch}>Excluir</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {adjustments.length > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-red-500/30 text-red-400 hover:bg-red-500/10">
                    <Trash2 className="w-4 h-4 mr-2" /> Excluir Tudo
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Excluir todas as despesas?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Esta ação é irreversível. Todas as {adjustments.length} despesas de plataforma serão removidas permanentemente.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteAll} className="bg-red-600 hover:bg-red-700">Excluir Tudo</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        <Card className="bg-white/5 border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : adjustments.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Nenhuma despesa de plataforma registrada.</p>
                <p className="text-sm mt-1">As despesas serão importadas automaticamente ao processar planilhas do iFood.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-transparent">
                      <TableHead className="w-10">
                        <input
                          type="checkbox"
                          checked={selectedIds.length === adjustments.length && adjustments.length > 0}
                          onChange={toggleSelectAll}
                          className="rounded border-gray-600"
                        />
                      </TableHead>
                      <TableHead className="text-gray-400">Data</TableHead>
                      <TableHead className="text-gray-400">Descrição</TableHead>
                      <TableHead className="text-gray-400">Pedido Associado</TableHead>
                      <TableHead className="text-gray-400">Operadora</TableHead>
                      <TableHead className="text-gray-400 text-right">Valor</TableHead>
                      <TableHead className="text-gray-400 w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map((adj) => {
                      const isPositive = parseSafeNumber(adj.valor_desconto) > 0;
                      return (
                      <TableRow key={adj.id} className="border-white/5 hover:bg-white/5">
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(adj.id)}
                            onChange={() => toggleSelect(adj.id)}
                            className="rounded border-gray-600"
                          />
                        </TableCell>
                        <TableCell className="text-gray-300">{formatDate(adj.data_ocorrencia)}</TableCell>
                        <TableCell className="text-gray-300 max-w-xs truncate" title={adj.descricao}>
                          {adj.descricao}
                        </TableCell>
                        <TableCell className="text-gray-400 font-mono text-xs">
                          {adj.codigo_pedido ? adj.codigo_pedido.substring(0, 8) + '...' : '—'}
                        </TableCell>
                        <TableCell className="text-gray-400">{adj.operators?.name || '—'}</TableCell>
                        <TableCell className={`text-right font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                          {isPositive ? '+' : ''}{formatCurrency(adj.valor_desconto)}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(adj.id)} className="text-gray-500 hover:text-red-400">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default PlatformAdjustmentsPage;
